import { gql } from '@apollo/client'

export const FRAGMENT_COMMUNICATION_SCHEDULES = gql`
  fragment CommunicationSchedulesFields on CommunicationSchedule {
    id
    days
    relation
    message
    messageSendAt
    timezone
    useBusinessDays
    title
    scheduledAtPreview
    updatedAt
    useDueDate
    position
    isActive
    validAfterDueDateDays
    dueDateType
    fixedDueDateDetailed {
      date
      time
      timezone
    }
    overrideDetailed {
      date
      time
      timezone
    }
    isOverridden
    communicationScheduleHistories {
      id
      sentAt
      scheduledAt
      error
    }
  }
`

export const FRAGMENT_COMMUNICATION_SCHEDULES_WITHOUT_PREVIEW = gql`
  fragment CommunicationSchedulesFieldsWithoutPreview on CommunicationSchedule {
    id
    days
    relation
    message
    messageSendAt
    timezone
    useBusinessDays
    title
    updatedAt
    useDueDate
    position
    isActive
    validAfterDueDateDays
    dueDateType
    fixedDueDateDetailed {
      date
      time
      timezone
    }
  }
`

export const FRAGMENT_COHORT_EMAIL_FIELDS = gql`
  fragment CohortEmailFields on CohortEmail {
    id
    title
    description
    clientId
    dateCohortId
    dateCohort {
      id
      name
      dateType {
        id
        name
      }
    }
    relation
    days
    useBusinessDays
    sendTo {
      label
      value
      type
    }
    emailFrom {
      label
      value
      type
    }
    messageSendAt
    timezone
    active
    createdAt
    updatedAt
    taxonomyTerms {
      id
      title
      key
      taxonomyId
    }
    excludedTaxonomyTerms {
      id
      title
      key
      taxonomyId
    }
    cohortEmailHistories {
      id
      sentAt
      scheduledAt
      error
    }
    userListFields {
      type
      key
      taxonomyId
      dateTypeId
      label
    }
  }
`

export const FRAGMENT_COHORT_SCHEDULE_FIELDS = gql`
  fragment CohortScheduleFields on CohortSchedule {
    id
    clientId
    dateCohortId
    relation
    days
    useBusinessDays
    fireAtTime
    timezone
    active
    templateTasks {
      id
      title
      internalName
      # ENGG-9617: the trigger form deep-links a no-longer-eligible task back to
      # its template page so the admin can see for themselves why it dropped out
      # (usually: it is inactive). workflow is the task's parent template, which
      # is the id the /dashboard/template/category/:id route expects.
      workflow {
        id
      }
    }
    templateCategories {
      id
      category
      internalCategory
    }
    createdAt
    updatedAt
  }
`

// common task fields
const commonTaskFields = `
  id
  position
  title
  internalName
  description
  taskType
  frequencyType
  locationHierarchyId
  createdAt
  rrule
  mediaUrl
  taskCompleted
  docusignTemplateId
  templateTaskId
  isTemplatePublished
  templateCategory
  multipleOptionsId
  dueDate
  dueDateTz
  eligibleFrom
  eligibleFromTz
  hideFromCustomer
  showNotApplicableOption
  emailFrom {
    label
    value
    type
  }
  replyTo {
    label
    value
    type
  }
  dependsOnTaskId
  ccTo {
    label
    value
    type
  }
  users {
    id
    email
  }
  userRoles {
    userId
    templateRoleId
  }
  templateRoles {
    id
    name
  }
  ignoreMissingSnippet
  forceImported
  forceImportedBy {
    id
    email
  }
  chatPersona {
    id
    name
    jobTitle
    company
    voice
    demeanor
    additionalBackground
    chatPersonaAvatar {
      id
      imageUrl
    }
  }
  chatPersonaId
  chatInstructions
  chatMaxScore
  chatPassingScore
  scoredByReviewerEnabled
  averageScoresEnabled
  averageReviewerScoresEnabled
  completeOnPassingScore
  roleplayAttemptsLimit
  optimizedInstructions
  optimizedInstructionsUpdatedAt
  optimizedInstructionsStale
  aiRoleplayTypeId
  aiRoleplayModel
  aiRoleplayId
  roleplayType
  calendarEventGuestModify
  calendarEventGuestInviteOthers
  calendarEventGuestSeeList
`

// fragment for schema tasks: __typename "Task"
export const FRAGMENT_TASK_FIELDS = gql`
  fragment TaskFields on Task {
    ${commonTaskFields}
    hasOwnDescription
    titleRaw
    descriptionRaw
    lastEvent {
      eventType
      countsAsCompletion
      percentage
      score
      totalScore
      completedAt
      completedByName
      completedByAdminName
      scheduledAt
      createdAt
      emailEvents {
        emailType
        eventType
        timestamp
      }
      isProcessing
      hasCustomEmailService
      employeeNotInvited
      employeeRemoved
      meetingOutcomes
      slackGroupErrors {
        id
        errorType
        rawError
        userFriendlyError
        retryable
        createdAt
      }
    }
    lastError {
      error
      level
      cause
      createdAt
    }
    hasNotificationConfigs
    slackEventType
    videoUrl
    audioUrl
    emailHeaderUrl
    emailFooterUrl
    emailBackgroundColor
    emailBorderColor
    centralizeEmail
    eligibleFromDetailed {
      date
      time
      timezone
    }
    dueDateDetailed {
      date
      time
      timezone
    }
    validAfterDueDateDays
    tasksOverrideValues {
      overrideValue {
        value
        taskPropertyName
      }
    }
    workflowId
    status
    slackGroup {
      id
      dueDate
      channelId
      dateType {
        id
        name
      }
    }
    templateTask {
      position
      templateTaskDateSetting {
        dateType {
          id
          name
        }
      }
      dependencySetting {
        triggerCondition
        triggerConditions
        scoreOperator
        scoreThreshold
        scoreThresholdType
        visibleWhileLocked
      }
    }
    offboarding
    slackFromUserId
  }
`

export const FRAGMENT_TEMPLATE_TASK_DATE_SETTINGS = gql`
  fragment TemplateTaskDateSettingsFields on TemplateTaskDateSetting {
    dateType {
      id
      name
      baseDateType {
        id
        name
      }
      targetDateCohort {
        id
        dateCohortDates {
          id
          name
          date
          toDate
          date
        }
      }
    }
    days
    relation
    useBusinessDays
    time
    timezone
  }
`

export const FRAGMENT_TEMPLATE_TASK_FIELDS = gql`
  fragment TemplateTaskFields on TemplateTask {
    id
    internalName
    title
    titleRaw
    description
    descriptionRaw
    taskType
    mediaUrl
    isTemplatePublished
    tasksCount
    groupTasksCount
    showNotApplicableOption
    inactive
    draft
    preBoarding
    offboarding
    important
    isDelegable
    skipDueDateOnSelfEnroll
    skipAssignDateOnSelfEnroll
    estimateInMins
    validAfterDueDateDays
    allowMultiSelect
    slackChannelsIds
    slackEventType
    slackChannelDescription
    slackCanvases {
      id
      content
      position
    }
    slackTeamId
    slackFacilitators
    slackAppendDateToChannelName
    videoUrl
    audioUrl
    emailHeaderUrl
    emailFooterUrl
    emailBackgroundColor
    emailBorderColor
    centralizeEmail
    emailFooterIconId
    emailHeaderIconId
    dependsOnTemplateTaskId
    dependencySetting {
      id
      templateTaskId
      triggerCondition
      triggerConditions
      questionOptionId
      scoreOperator
      scoreThreshold
      scoreThresholdType
      visibleWhileLocked
    }
    doceboCourseId
    iconLibraryId
    docusignTemplateId
    multipleOptionsId
    templateCategoryId
    templateCategory {
      id
      ancestry
      category
      internalCategory
      inactive
      position
    }
    templateRoles {
      id
      name
    }
    assignedUsers {
      id
      email
    }
    taxonomyTerms {
      id
      title
      key
      taxonomyId
    }
    excludedTaxonomyTerms {
      id
      title
      taxonomyId
    }
    matchMode
    templateTaskDateSetting {
      ...TemplateTaskDateSettingsFields
    }
    assignDate {
      ...TemplateTaskDateSettingsFields
    }
    notifyOnComplete {
      label
      value
      type
    }
    notifyOnCantComplete {
      label
      value
      type
    }
    ccTo {
      label
      value
      type
    }
    emailFrom {
      label
      value
      type
    }
    replyTo {
      label
      value
      type
    }
    questions {
      id
      title
      required
      description
      explanation
      order
      quizType
      allowOtherOption
      updatedAt
      templateTaskQuestionSectionId
      dependsOnQuestionOptionIds
      metadata
      options {
        id
        order
        title
        description
        score
        correct
        taxonomyId
        dateTypeId
        roleId
        propertyName
        autoLock
      }
    }
    amountOfRequiredQuestions
    amountOfRetriesAllowed
    contentItemId
    contentItem {
      id
      title
      description
      descriptionRaw
      contentItemsSectionId
    }
    questionSetId
    questionSet {
      id
      name
      templateTaskType
      position
      contentItemsSectionId
    }
    scormCourse {
      id
      title
      uploadState
      uploadError
      filename
      contentItemsSectionId
    }
    scormReuseCompletion
    scormReuseCompletionMonths
    videoRecord {
      id
      title
      videoUrl
      filename
      contentItemsSectionId
    }
    templateTaskCalendarEvents {
      calendarEventId
      calendarOwnerEmail
      calendarId
      __typename
    }
    calendarEventOrganizerTemplateRole {
      id
      name
      __typename
    }
    calendarConferenceOptions {
      __typename
      ... on ZoomMeetingOptions {
        security
        registrationRequired
        joinBeforeHost
        autoRecording
        muteUponEntry
        questionAndAnswer
        alternativeHosts
        zoomDescription
        templateId
        thirdPartySurvey
      }
      ... on ZoomWebinarOptions {
        altHosts
        altHostsEditPolls
        authAttendees
        authPanelists
        autoRecord
        emailInAttendeeReport
        hdScreenShare
        onDemand
        practiceSession
        qna
        recordLocation
        registrationRequired
        requirePasscode
        videoHost
        videoPanelists
        zoomDescription
        templateId
        thirdPartySurvey
      }
      ... on MicrosoftTeamsOptions {
        autoRecord
        coOrganizers
      }
    }
    calendarId
    calendarEventType
    sessionTemplateTaskIds
    sessionOptionsLimit
    calendarEventDuration
    calendarEventSmartScheduling {
      enabled
      searchForDays
      fromHour
      toHour
    }
    calendarEventFacilitators {
      assignmentMode
      considerAvailability
      facilitators
      notifyOnFacilitatorDecline
      fallbackFacilitators
      temporaryFacilitators {
        dateRange
        emails
      }
    }
    calendarEventConferenceTool
    calendarEventLocation
    calendarOwnerEmail
    calendarEventSkipAssignmentInvite
    optionalTemplateRolesIds
    optionalAssignedUserIds
    calendarEventCalendarGroups
    calendarResources {
      name
      email
    }
    calendarResourcesAssignmentMode
    calendarCategories {
      id
      name
    }
    calendarEventVisibility
    calendarEventOptions {
      availability
      allDay
    }
    zoomOwnerEmail
    zoomTemplatePresetId
    owner {
      id
      fullName
      email
    }
    excludedTaxonomyTerms {
      id
      taxonomyId
    }
    sendOverdueEmail
    templateTaskOverrideRules {
      id
    }
    videoPercentageComplete
    videoAllowSkip
    videoAllowManualComplete
    audioPercentageComplete
    audioAllowSkip
    audioAllowManualComplete
    communicationSchedules {
      ...CommunicationSchedulesFieldsWithoutPreview
    }
    taskNotificationConfigs {
      id
      triggerType
      direction
      offsetValue
      offsetUnit
      mediums
      message
      active
      position
      recipients {
        label
        value
        type
      }
      ccTo {
        label
        value
        type
      }
      sendFrom {
        label
        value
        type
      }
      recipientEmailTarget
      fixedSendDateDetailed {
        date
        time
        timezone
      }
    }
    attendeeMode
    calendarEventGuestModify
    calendarEventGuestInviteOthers
    calendarEventGuestSeeList
    slackFromUserId
    calendarEventNotifyAttendees
    sendRecordingToAbsentees
    fixedDueDateDetailed {
      date
      time
      timezone
    }
    dueDateType
    fixedAssignDateDetailed {
      date
      time
      timezone
    }
    assignDateType
    chatPersona {
      id
      name
      jobTitle
      company
      voice
      demeanor
      additionalBackground
      chatPersonaAvatar {
        id
        imageUrl
      }
    }
    chatPersonaId
    chatInstructions
    chatMaxScore
    chatPassingScore
    scoredByReviewerEnabled
    averageScoresEnabled
    averageReviewerScoresEnabled
    scoredByTemplateRolesIds
    scoredByUserIds
    scoredByTemplateRoles {
      id
      name
    }
    scoredByUsers {
      id
      email
      firstName
      lastName
    }
    completeOnPassingScore
    roleplayAttemptsLimit
    optimizedInstructions
    optimizedInstructionsUpdatedAt
    optimizedInstructionsStale
    aiRoleplayTypeId
    aiRoleplayModel
    aiRoleplayId
    aiRoleplay {
      id
      name
      libraryTitle
      contentItemsSectionId
    }
    roleplayType
    ignoreMissingSnippet
    workflow {
      id
      category
      internalCategory
      inactive
    }
    section {
      id
      category
      internalCategory
      inactive
    }
    certificateId
    certificateAwardPaused
    certificatePausedRuleId
    badgeAwardPaused
    badgePausedRuleId
    badgeId
  }
  ${FRAGMENT_TEMPLATE_TASK_DATE_SETTINGS}
  ${FRAGMENT_COMMUNICATION_SCHEDULES_WITHOUT_PREVIEW}
`

export const FRAGMENT_USER_FIELDS = gql`
  fragment UserFields on User {
    id
    email
    personalEmail
    firstName
    lastName
    fullName
    isConfidentialNewHire
    isTemp
    startDate
    homeAddress
    phoneNumber
    mainClient {
      id
      name
    }
    manager {
      id
      email
      firstName
      lastName
      fullName
    }
    isMainClient
    isMainClientAdmin: isAdmin
    isAdmin: isAdminByClient
    taxonomyTerms: taxonomyTermsByClient {
      id
      taxonomyId
    }
    taxonomyTermsAdmins {
      id
      taxonomyId
    }
    role {
      id
      name
    }
    welcomeEmailSentAt
    personalWelcomeEmailSentAt
    upn
    externalId
    employeeId
    terminationDate
    externalUpdateDisabled
    practiceEnabled
    userDates {
      date
      dateType {
        id
      }
    }
    customFields
    isTerminated
    preboarding
    employeeState
    customFields
    avatarUrl
    excludeSso
    lockedProperties {
      user
      userDate
      taxonomy
      customField
      userTemplateRolesAssignee
    }
    userTemplateRolesAssignees {
      templateRole {
        id
        name
      }
      assignee {
        id
        email
        fullName
      }
    }
  }
`

export const FRAGMENT_USER_DATES_ROLES_FIELDS = gql`
  fragment UserDatesAndRolesFields on User {
    userDates {
      date
      dateType {
        id
      }
    }
    cohortMemberships {
      id
      source
      dateCohort {
        id
        name
      }
    }
    userTemplateRolesAssignees {
      templateRole {
        id
      }
      user {
        id
        fullName
        email
      }
      assignee {
        id
        fullName
        email
      }
    }
  }
`

export const FRAGMENT_USER_OWNER_FIELDS = gql`
  fragment UserOwnerFields on User {
    id
    firstName
    lastName
    fullName
    email
    workEmail
    personalEmail
    taxonomyTerms {
      title
      id
      taxonomy {
        name
        slug
      }
    }
    userDates {
      id
      date
      dateType {
        id
        name
      }
    }
  }
`

export const FRAGMENT_USER_AUTOCOMPLETE_FIELDS = gql`
  fragment UserAutocompleteFields on User {
    id
    email
    firstName
    lastName
    fullName
  }
`

export const FRAGMENT_PAGINATION_META_FIELDS = gql`
  fragment PaginationMetaFields on PaginationMeta {
    page
    perPage
    totalPages
    totalCount
    nextPage
    prevPage
  }
`

export const FRAGMENT_EXTERNAL_USER_AUTOCOMPLETE_WITH_ASSIGNMENTS_FIELDS = gql`
  fragment UserAutocompleteFields on ExternalUser {
    email
    firstName
    lastName
    fullName
    externalId
    userId
  }
`

export const FRAGMENT_TEMPLATE_CATEGORY_FOLDER = gql`
  fragment TemplateCategoryFolderFields on TemplateCategoryFolder {
    id
    title
    position
    parentFolder {
      id
      title
    }
    subfolders {
      id
      title
      position
    }
  }
`

// Read-only Events rows for the employee drawer's Events tab (ENGG-9422).
// Deliberately much smaller than LocationHierarchyFields: nothing on this tab is
// editable, so the dependency, expiry and eligibility fields that drive the
// Workflows tree's actions are not fetched. `partnerEventId` is what lets each
// per-event section link through to the event itself.
export const FRAGMENT_EVENT_WORKFLOW_FIELDS = gql`
  fragment EventWorkflowFields on LocationHierarchy {
    id
    title
    createdAt
    templateCategoryId
    partnerEventId
    children {
      id
      title
      templateCategoryId
      partnerEventId
    }
  }
`

export const FRAGMENT_LOCATION_HIERARCHY_FIELDS = gql`
  fragment LocationHierarchyFields on LocationHierarchy {
    id
    templateCategoryId
    title
    createdAt
    deletedAt
    isExpired
    dependsOnId
    eligibleFrom
    isTopLevelNode
    children {
      id
      title
      isExpired
      templateCategoryId
      dependsOnId
      eligibleFrom
      templateCategory {
        id
        category
        internalCategory
        inactive
        position
      }
      dependsOn {
        id
        title
      }
    }
    dependsOn {
      id
      title
    }
    templateCategory {
      id
      category
      internalCategory
      inactive
      position
    }
    owner {
      id
      firstName
      lastName
      fullName
      email
      personalEmail
    }
  }
`
export const FRAGMENT_GROUP_TASK_FIELDS = gql`
  fragment GroupTaskFields on GroupTask {
    id
    state {
      status
      message
      createdAt
    }
    baseDateType {
      id
      name
    }
    dueDate
    baseDateValue
    templateTask {
      id
      title
      internalName
      dueDateType
      position
      templateCategory {
        id
        internalCategory
        position
      }
      calendarEventFacilitators {
        facilitators
        temporaryFacilitators {
          dateRange
          emails
        }
      }
      templateTaskDateSetting {
        time
        timezone
      }
      fixedDueDateDetailed {
        timezone
      }
      calendarResources {
        email
        name
      }
      calendarResourcesAssignmentMode
    }
    metadata {
      blackoutDates
    }
  }
`

export const FRAGMENT_BADGE_FIELDS = gql`
  fragment BadgeFields on Badge {
    id
    clientId
    position
    name
    description
    enabled
    shape
    eyebrowText
    eyebrowVisible
    title
    titleVisible
    iconName
    iconVisible
    iconLibraryId
    iconLibraryUrl
    logoImageUrl
    customImageUrl
    background
    outerBorder
    innerBorder
    iconColor
    accentColor
    textColor
    eyebrowColor
    titleColor
    iconScale
    logoScale
  }
`

export const FRAGMENT_CERTIFICATE_FIELDS = gql`
  fragment CertificateFields on Certificate {
    id
    clientId
    position
    badgeId
    name
    description
    enabled
    courseName
    issuingAuthority
    signatoryName
    signatoryRole
    signatorySignatureImageUrl
    issuerLogoUrl
    customImageUrl
    background
    backgroundImageUrl
    backgroundImageVisible
    cardBackground
    borderColor
    textColor
    accentColor
    headerText
    awardedToLabel
    completingLabel
    footerVisible
    footerText
    headerColor
    awardedToColor
    recipientColor
    completingLabelColor
    courseNameColor
    dateColor
    footerColor
    signatoryColor
    issuerLogoScale
    badgeScale
    awardBasis
    sendEmailOnCompletion
    emailSubject
    emailBody
  }
`

// ENGG-9010 — what drawing a certificate needs (CertificateArtwork), without
// the award-email fields. The templates list, the task table and the Rules tab
// draw certificates on every row; carrying emailBody on each was weight for
// nothing, and the target picker loads the task fragment once per category.
export const FRAGMENT_CERTIFICATE_ARTWORK_FIELDS = gql`
  fragment CertificateArtworkFields on Certificate {
    id
    clientId
    badgeId
    name
    enabled
    courseName
    issuingAuthority
    signatoryName
    signatoryRole
    signatorySignatureImageUrl
    issuerLogoUrl
    customImageUrl
    background
    backgroundImageUrl
    backgroundImageVisible
    cardBackground
    borderColor
    textColor
    accentColor
    headerText
    awardedToLabel
    completingLabel
    footerVisible
    footerText
    headerColor
    awardedToColor
    recipientColor
    completingLabelColor
    courseNameColor
    dateColor
    footerColor
    signatoryColor
    issuerLogoScale
    badgeScale
    awardBasis
  }
`

// ENGG-9010 — one award rule as the Rules tab renders it. Shared by the list
// query and the upsert's response: when the response carried less than the
// list (grants without artwork), Apollo could not complete the cached row from
// it and refetched the whole list, flashing the table back to its loader on
// every park / re-activate.
export const FRAGMENT_AWARD_CONDITION_FIELDS = gql`
  fragment AwardConditionFields on AwardCondition {
    id
    name
    origin
    kind
    active
    awardedCount
    earnedEmployeeCount
    createdAt
    grants {
      id
      kind
      badgeId
      certificateId
      name
      badge {
        ...BadgeFields
      }
      certificate {
        ...CertificateArtworkFields
      }
    }
    targets {
      id
      level
      label
      conditionKind
      threshold
      templateCategoryId
      templateTaskId
      taskType
      templateId
      templateName
    }
    taskTypeQuotas {
      id
      taskType
      requiredCount
      conditionKind
      threshold
      countCompletionsFrom
    }
    badgeRequirements {
      id
      badgeId
      name
      badge {
        ...BadgeFields
      }
    }
    countBadgesFrom
    templates {
      id
      name
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
  ${FRAGMENT_CERTIFICATE_ARTWORK_FIELDS}
`

export const FRAGMENT_TEMPLATE_CATEGORY_LIST_FIELDS = gql`
  fragment TemplateCategoryListFields on TemplateCategory {
    id
    position
    category
    categoryRaw
    internalCategory
    notifyOnCantComplete {
      label
      value
      type
    }
    notifyOnFacilitatorDecline {
      label
      value
      type
    }
    notifyOnCertificate {
      label
      value
      type
    }
    createdAt
    inactive
    expireAfterDays
    timeToComplete
    templateTags {
      id
      name
      color
    }
    owner {
      ...UserFields
    }
    templateCategoryFolder {
      id
      title
    }
    disableNewAssignmentEmails
    disableTaskNotes
    disableCantComplete
    isEnrollable
    enrollmentDateCohortId
    enrollmentDateCohort {
      id
      name
      dateCohortDates {
        id
        name
        date
        toDate
        capacity
        enrolledCount
        availableSlots
      }
    }
    enrollmentCapacity
    autoReimport
    taxonomyTerms {
      id
      title
      taxonomyId
    }
    excludedTaxonomyTerms {
      id
      title
      taxonomyId
    }
    imageUrl
    description
    shortDescription
    whatYouWillLearn
    isFeatured
    isRecommended
    courseContentType {
      id
      name
    }
    courseFormat {
      id
      name
    }
    courseLevel {
      id
      name
    }
    courseSkills {
      id
      name
    }
    courseCategories {
      id
      name
    }
    enrollmentsCount
    completedCount
    certificateId
    certificateAwardPaused
    certificatePausedRuleId
    badgeAwardPaused
    badgePausedRuleId
    certificate {
      id
      name
      courseName
      # ENGG-9010 — the design fields, so the Certificate column can draw the
      # certificate itself (CertificateBadgeCell artwork). Same row, no join.
      ...CertificateArtworkFields
      badge {
        ...BadgeFields
      }
    }
    badgeId
    badge {
      ...BadgeFields
    }
    ancestry
    blackoutDates
  }
  ${FRAGMENT_USER_FIELDS}
  ${FRAGMENT_BADGE_FIELDS}
  ${FRAGMENT_CERTIFICATE_ARTWORK_FIELDS}
`

// Shape needed by the admin Badges tab — any UserCertification (auto-issued
// or manually assigned) plus everything we need to render the badge
// thumbnail and label on the grid card. Server resolver:
// `User#assignedBadges` (ENGG-8858). The `manual` flag distinguishes the
// two sources so the UI can only offer removal on manual rows.
// A standalone UserBadge award (ENGG-8989) — no accompanying
// certificate. Sibling of AssignedBadgeFields, which is on
// UserCertification and always carries a certificate.
export const FRAGMENT_USER_BADGE_FIELDS = gql`
  fragment UserBadgeFields on UserBadge {
    id
    badgeId
    issuedAt
    manual
    scheduled
    processing
    badgePngDownloadUrl
    publicBadgeUrl
    sourceLabel
    templateCategoryId
    templateTaskId
    templateTaskWorkflowId
    recipientName
    assignedBy {
      id
      firstName
      lastName
    }
    badge {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const FRAGMENT_ASSIGNED_BADGE_FIELDS = gql`
  fragment AssignedBadgeFields on UserCertification {
    id
    badgeId
    certificateId
    issuedAt
    manual
    scheduled
    processing
    publicCertificateUrl
    certificatePdfDownloadUrl
    badgePngDownloadUrl
    sourceLabel
    templateCategoryId
    templateTaskId
    templateTaskWorkflowId
    recipientName
    assignedBy {
      id
      firstName
      lastName
    }
    # ENGG-10188 — the artwork fields too: the Certificates tab draws the
    # certificate itself (CertificateArtwork), like the catalog does.
    certificate {
      ...CertificateArtworkFields
    }
    # The snapshot badge — the state of the Badge at issuance time. Do
    # NOT read certificate.badge here, since that follows the live
    # association and will diverge if an admin swaps the certificate's
    # badge later. See UserCertificationType#badge on the backend.
    badge {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
  ${FRAGMENT_CERTIFICATE_ARTWORK_FIELDS}
`

export const FRAGMENT_TEMPLATE_CATEGORY_BASE_FIELDS = gql`
  fragment TemplateCategoryBaseFields on TemplateCategory {
    id
    category
    internalCategory
    inactive
    clientId
    ancestry
    blackoutDates
  }
`

export const FRAGMENT_TEMPLATE_CATEGORY_WITH_TASKS = gql`
  fragment TemplateCategoryWithTasks on TemplateCategory {
    ...TemplateCategoryBaseFields
    templateTasks {
      id
      title
      description
      taskType
      internalName
      mediaUrl
      position
      inactive
      important
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_BASE_FIELDS}
`

export const FRAGMENT_TEMPLATE_CATEGORY_WITH_CHILDREN = gql`
  fragment TemplateCategoryWithChildren on TemplateCategory {
    ...TemplateCategoryWithTasks
    children {
      ...TemplateCategoryWithTasks
      children {
        ...TemplateCategoryWithTasks
      }
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_WITH_TASKS}
`

export const FRAGMENT_TEMPLATE_TASK_FOR_FILTERS = gql`
  fragment TemplateTaskForFilters on TemplateTask {
    id
    taskType
    templateTaskDateSetting {
      dateType {
        id
        name
      }
    }
    taxonomyTerms {
      taxonomyId
    }
    templateRoles {
      id
      name
    }
  }
`

export const FRAGMENT_TEMPLATE_TASK_FOR_ROLES_AND_DATES = gql`
  fragment TemplateTaskForRolesAndDates on TemplateTask {
    id
    taskType
    inactive
    calendarEventType
    dueDateType
    templateTaskDateSetting {
      dateType {
        id
        name
        baseDateType {
          id
          name
        }
        targetDateCohort {
          id
          dateCohortDates {
            id
            name
            toDate
            date
          }
        }
      }
    }
    assignDate {
      dateType {
        id
        name
        baseDateType {
          id
          name
        }
        targetDateCohort {
          id
          dateCohortDates {
            id
            name
            toDate
            date
          }
        }
      }
    }
    templateRoles {
      id
      name
    }
    taxonomyTerms {
      id
      taxonomyId
    }
    ccTo {
      type
      value
      label
    }
    emailFrom {
      type
      value
      label
    }
    replyTo {
      type
      value
      label
    }
    notifyOnCantComplete {
      type
      value
      label
    }
    notifyOnComplete {
      type
      value
      label
    }
  }
`

export const FRAGMENT_TEMPLATE_CATEGORY_WITH_FILTER_TASKS = gql`
  fragment TemplateCategoryWithFilterTasks on TemplateCategory {
    ...TemplateCategoryBaseFields
    templateTasks {
      ...TemplateTaskForFilters
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_BASE_FIELDS}
  ${FRAGMENT_TEMPLATE_TASK_FOR_FILTERS}
`

export const FRAGMENT_TEMPLATE_CATEGORY_WITH_ROLES_AND_DATES_TASKS = gql`
  fragment TemplateCategoryWithRolesAndDatesTasks on TemplateCategory {
    ...TemplateCategoryBaseFields
    templateTasks {
      ...TemplateTaskForRolesAndDates
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_BASE_FIELDS}
  ${FRAGMENT_TEMPLATE_TASK_FOR_ROLES_AND_DATES}
`

export const FRAGMENT_TEMPLATE_CATEGORY_HIERARCHY_FOR_FILTERS = gql`
  fragment TemplateCategoryHierarchyForFilters on TemplateCategory {
    ...TemplateCategoryWithFilterTasks
    children {
      ...TemplateCategoryWithFilterTasks
      children {
        ...TemplateCategoryWithFilterTasks
      }
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_WITH_FILTER_TASKS}
`

export const FRAGMENT_TEMPLATE_CATEGORY_HIERARCHY_FOR_ROLES_AND_DATES = gql`
  fragment TemplateCategoryHierarchyForRolesAndDates on TemplateCategory {
    ...TemplateCategoryWithRolesAndDatesTasks
    children {
      ...TemplateCategoryWithRolesAndDatesTasks
      children {
        ...TemplateCategoryWithRolesAndDatesTasks
      }
    }
  }
  ${FRAGMENT_TEMPLATE_CATEGORY_WITH_ROLES_AND_DATES_TASKS}
`

// ENGG-8443: shared task fields used by both the lazy per-section task
// query (TEMPLATE_TASKS_BY_CATEGORY) and the on-the-wire shape that was
// previously inlined into the now-trimmed FRAGMENT_TEMPLATE_CATEGORY_FOR_SECTIONS.
export const FRAGMENT_TEMPLATE_TASK_FOR_SECTION = gql`
  fragment TemplateTaskForSection on TemplateTask {
    calendarEventType
    calendarEventDuration
    draft
    titleRaw
    descriptionRaw
    id
    title
    taskType
    internalName
    mediaUrl
    position
    inactive
    important
    preBoarding
    offboarding
    isDelegable
    skipDueDateOnSelfEnroll
    skipAssignDateOnSelfEnroll
    dueDateType
    dependsOnTemplateTaskId
    templateCategoryId
    videoRecord {
      id
      title
      videoUrl
      filename
      contentItemsSectionId
    }
    taxonomyTerms {
      id
      title
      key
      taxonomyId
    }
    excludedTaxonomyTerms {
      id
      title
      key
      taxonomyId
    }
    matchMode
    templateRoles {
      id
      name
    }
    optionalTemplateRolesIds
    optionalAssignedUserIds
    assignedUsers {
      id
      email
    }
    templateTaskDateSetting {
      days
      relation
      dateType {
        id
        name
      }
      useBusinessDays
      time
      timezone
    }
    assignDate {
      days
      relation
      dateType {
        id
        name
      }
      useBusinessDays
      time
      timezone
    }
    fixedDueDateDetailed {
      date
      time
      timezone
    }
    fixedAssignDateDetailed {
      date
      time
      timezone
    }
    assignDateType
    owner {
      id
      fullName
      email
    }
    calendarEventConferenceTool
    calendarConferenceOptions {
      __typename
      ... on ZoomMeetingOptions {
        security
        registrationRequired
        joinBeforeHost
        autoRecording
        muteUponEntry
        questionAndAnswer
        alternativeHosts
        zoomDescription
        templateId
        thirdPartySurvey
      }
      ... on ZoomWebinarOptions {
        altHosts
        altHostsEditPolls
        authAttendees
        authPanelists
        autoRecord
        emailInAttendeeReport
        hdScreenShare
        onDemand
        practiceSession
        qna
        recordLocation
        registrationRequired
        requirePasscode
        videoHost
        videoPanelists
        zoomDescription
        templateId
      }
      ... on MicrosoftTeamsOptions {
        autoRecord
        coOrganizers
      }
    }
    calendarEventFacilitators {
      assignmentMode
      considerAvailability
      facilitators
      fallbackFacilitators
      notifyOnFacilitatorDecline
      temporaryFacilitators {
        dateRange
        emails
      }
    }
    certificateId
    certificateAwardPaused
    certificatePausedRuleId
    badgeAwardPaused
    badgePausedRuleId
    certificate {
      id
      name
      courseName
      # ENGG-9010 — the design fields, so the Certificate column can draw the
      # certificate itself (CertificateBadgeCell artwork). Same row, no join.
      ...CertificateArtworkFields
      badge {
        ...BadgeFields
      }
    }
    badgeId
    badge {
      ...BadgeFields
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
  ${FRAGMENT_CERTIFICATE_ARTWORK_FIELDS}
`

// ENGG-8443: section-only shape. templateTasks deliberately omitted from
// both root and children; tasks are fetched per-section on expand via
// TEMPLATE_TASKS_BY_CATEGORY.
//
// templateTasksCount carries the filter-aware count so collapsed section
// headers can render "N Tasks" without paying for the per-section list,
// and so the hide-when-search-has-no-matches behavior works for sections
// that haven't been expanded yet. The $showInactive / $searchQuery
// variables are threaded from the parent useQuery in TemplateSectionsHome.
export const FRAGMENT_TEMPLATE_CATEGORY_FOR_SECTIONS = gql`
  fragment TemplateCategoryForSections on TemplateCategory {
    id
    category
    internalCategory
    categoryRaw
    ancestry
    inactive
    dependsOnId
    visibleWhileLocked
    isEnrollable
    templateCategoryFolderId
    templateCategoryFolder {
      id
      title
      parentFolder {
        id
        title
      }
    }
    templateTasksCount(
      showInactive: $showInactive
      searchQuery: $searchQuery
      filterModel: $filterModel
    )
    certificateId
    certificateAwardPaused
    certificatePausedRuleId
    badgeAwardPaused
    badgePausedRuleId
    certificate {
      id
      name
      courseName
      badge {
        ...BadgeFields
      }
    }
    badgeId
    badge {
      ...BadgeFields
    }
    taxonomyTerms {
      id
      title
      key
      taxonomyId
    }
    excludedTaxonomyTerms {
      id
      title
      key
      taxonomyId
    }
    children {
      id
      category
      internalCategory
      categoryRaw
      ancestry
      inactive
      dependsOnId
      visibleWhileLocked
      isEnrollable
      templateTasksCount(
        showInactive: $showInactive
        searchQuery: $searchQuery
        filterModel: $filterModel
      )
      certificateId
      certificateAwardPaused
      certificatePausedRuleId
      badgeAwardPaused
      badgePausedRuleId
      certificate {
        id
        name
        badge {
          ...BadgeFields
        }
      }
      badgeId
      badge {
        ...BadgeFields
      }
      taxonomyTerms {
        id
        title
        key
        taxonomyId
      }
      excludedTaxonomyTerms {
        id
        title
        key
        taxonomyId
      }
    }
  }
  ${FRAGMENT_BADGE_FIELDS}
`

export const FRAGMENT_SNIPPET_FIELDS = gql`
  fragment SnippetFields on Snippet {
    id
    name
    value
    fallbackValue
    snippetType
  }
`

export const FRAGMENT_SNIPPET_CONDITIONS_FIELDS = gql`
  fragment SnippetConditionsFields on SnippetCondition {
    id
    value
    taxonomyTerms {
      id
      title
      taxonomy {
        id
        name
      }
    }
    excludedTaxonomyTerms {
      id
      title
      taxonomy {
        id
        name
      }
    }
    dateSettings {
      id
      fromDate
      toDate
      dateType {
        id
        name
      }
    }
  }
`

// ENGG-9979 — a Call Scorecards behavior, as the library list and the editor read it.
// ENGG-10221 — a client's qualification method (MEDDIC, MEDIC, …).
export const FRAGMENT_CALL_SCORECARD_METHODOLOGY_FIELDS = gql`
  fragment CallScorecardMethodologyFields on CallScorecardMethodology {
    name
    letters {
      key
      name
      codes
      from
    }
    roles
    newTypeBehaviors
  }
`

export const FRAGMENT_CALL_BEHAVIOR_FIELDS = gql`
  fragment CallBehaviorFields on CallBehavior {
    id
    code
    stage
    stageName
    name
    shortLabel
    definition
    levels
    applies
    trigger
    notes
    weight
    enabled
    custom
    edited
    appliesEdited
    kind
    details
  }
`

// ENGG-9829 / ENGG-10240 / ENGG-10296 / ENGG-10421 — everything the Sales automation card reads, asked by
// the query and returned by the upsert alike, so a save writes the whole saved
// row straight into the query's cache entry instead of reading it again.
export const FRAGMENT_SALES_AUTOMATION_SETTING_FIELDS = gql`
  fragment SalesAutomationSettingFields on SalesAutomationSetting {
    available
    callIngestionEnabled
    creationMode
    lookbackMinutes
    maxLookbackMinutes
    lastSweptAt
    ingestionReady
    defaultRoomTemplate {
      id
      name
    }
    roomTemplateOptions {
      id
      name
    }
    fallbackOwner {
      id
      email
      fullName
    }
    fallbackOwnerOptions {
      id
      email
      fullName
    }
    organizerEmails
    callKinds
  }
`

// ENGG-8024 — a Settings > Public Pages row.
export const PUBLIC_EMPLOYEE_FORM_FIELDS = `
  id
  name
  title
  introText
  thankYouMessage
  active
  shareToken
  submissionCount
  lastSubmissionAt
  ownerName
  createdAt
  fields {
    kind
    ref
    required
    available
  }
`

// ENGG-10450 — a personalized copy of a Content Hub document (Google Slides /
// Docs), as the admin dialog's Variants tab and the run poller read it. `item`
// is the copy as a Content Hub item (null until the run is `ready`); `source`
// is the document it was made from. The browse-row subset lives inline in the
// browse / asset queries as `variantOf`.
export const FRAGMENT_CONTENT_VARIANT_FIELDS = gql`
  fragment ContentVariantFields on ContentVariant {
    id
    state
    error
    outdated
    sourceChangedAt
    generatedAt
    refreshedAt
    createdAt
    createdBy {
      id
      firstName
      lastName
      email
    }
    customer {
      id
      name
      domain
    }
    roomId
    room {
      id
      name
    }
    instructions
    substitutionsCount
    logoReplaced
    driveUrl
    source {
      id
      source
      title
      url
      fileType
      thumbnailUrl
    }
    item {
      id
      source
      title
      url
      fileType
      thumbnailUrl
      lastModified
      createdAt
      ownerEmail
      customerId
      customerName
    }
    roomsUsing
    shareLinksCreated
    shareLinkClicks
    sharedWith
    roomOpens
  }
`

// ENGG-10450 — the subset of a variant a Content Hub item carries when it IS a
// copy: who it was personalized for, the document it came from, and whether
// that document has changed since (the "Personalized for … · from …" line and
// the Outdated chip on the card and in the file window).
export const FRAGMENT_CONTENT_VARIANT_OF_FIELDS = gql`
  fragment ContentVariantOfFields on ContentVariant {
    id
    outdated
    customer {
      id
      name
      domain
    }
    source {
      id
      source
      title
      url
      fileType
      thumbnailUrl
    }
  }
`
